import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { SearchComponent, toast } from '@kinet-open-network/client/shared/ui';
import {
  FieldArrayType,
  FieldArrayTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';

interface RepeatTableProps extends FormlyFieldProps {
  addButton: string | false;
  addButtonAction?: (field: FormlyFieldConfig, currentItem?: any) => void;
  addButtonDisabled?: boolean;
  removeText: string;
  removeDisabled?: boolean;
  removeHidden?: boolean;
  tableClass?: string;
  buttonClass?: string;
  buttonIcon?: string;
  cellClass?: string | Record<string, string>;
  headerCellClass?: string | Record<string, string>;
  newItem?: (currentItem?: any) => any;
  copyItem?: (
    field: FormlyFieldConfig,
    context: {
      origin: any;
      index: number;
      add: (
        ...args: Parameters<FieldArrayType['add']>
      ) => FormlyFieldConfig | undefined;
    },
  ) => void;
  rowsLimit?: number;
  buttonsDisabled?: boolean;
  allowSort?: boolean;
  allowFilter?: boolean;
  onDelete?: (field: FormlyFieldConfig, index: number) => void;
}

export interface FormlyRepeatTableFieldConfig
  extends FieldArrayTypeConfig<RepeatTableProps> {}

@Component({
  selector: 'kon-repeat-table-type',
  imports: [
    CommonModule,
    FormlyModule,
    ButtonModule,
    TieredMenuModule,
    TooltipModule,
    SearchComponent,
  ],
  template: `
    <div class="mb-3 overflow-x-auto">
      @if (props.label) {
        <legend>{{ props.label }}</legend>
      }
      @if (props.description) {
        <p>{{ props.description }}</p>
      }
      @if (props.allowFilter) {
        <div class="mb-2">
          <kon-search (searchChange)="applyFilter($event)"></kon-search>
        </div>
      }
      <table [class]="props.tableClass">
        <thead>
          <tr>
            @for (field of fields(); track field) {
              <th [ngClass]="['font-normal', getHeaderCellClass(field) ?? '']">
                {{ getLabel(field.key)
                }}{{ field.props?.required ? '&nbsp;*' : '' }}
              </th>
            }
            <th class="w-12">
              @if (props.allowSort) {
                <button
                  pButton
                  size="small"
                  type="button"
                  pTooltip="Zoradiť"
                  icon="pi pi-sort-alt"
                  class="p-button-text"
                  (click)="sortMenu.toggle($event)"
                ></button>

                <p-tieredMenu
                  #sortMenu
                  appendTo="body"
                  [popup]="true"
                  [model]="sortMenuModel"
                >
                </p-tieredMenu>
              }
            </th>
          </tr>
        </thead>
        <tbody>
          @for (rowField of field.fieldGroup; track rowField; let i = $index) {
            <tr
              [hidden]="isRowHidden(i)"
              (keydown)="onRowKeydown($event, $index)"
            >
              @if (rowField.fieldGroup) {
                @for (colField of rowField.fieldGroup; track colField) {
                  <td [class]="this.getCellClass(colField)">
                    <formly-field
                      [field]="removeLabel(colField)"
                    ></formly-field>
                  </td>
                }
              } @else {
                <td [class]="this.getCellClass()">
                  <formly-field [field]="removeLabel(rowField)"></formly-field>
                </td>
              }
              <td class="flex items-stretch pl-1">
                @if (props.copyItem) {
                  <button
                    pButton
                    type="button"
                    [disabled]="
                      formControl.disabled ||
                      props.buttonsDisabled ||
                      (field.fieldGroup?.length ?? 0) >=
                        (props.rowsLimit ?? 1000)
                    "
                    (click)="copyRowAt($index)"
                    icon="pi pi-copy"
                    [text]="true"
                    pTooltip="Kopírovať (Ctrl+D)"
                    tooltipPosition="top"
                    class="self-start"
                  ></button>
                }
                @if (!props.removeHidden) {
                  <button
                    pButton
                    type="button"
                    [label]="props.removeText"
                    [disabled]="
                      formControl.disabled ||
                      props.buttonsDisabled ||
                      props.removeDisabled
                    "
                    (click)="removeRowAt($index)"
                    icon="pi pi-times"
                    [text]="true"
                    severity="danger"
                    class="self-start"
                    pTooltip="Odstrániť (Ctrl+X)"
                    tooltipPosition="top"
                  ></button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>

      @if (props.addButton !== false) {
        <div class="w-full relative">
          <p-button
            [disabled]="
              props.disabled ||
              (this.field.fieldGroup?.length ?? 0) >=
                (this.props.rowsLimit ?? 1000) ||
              props.buttonsDisabled ||
              props.addButtonDisabled ||
              formControl.disabled
            "
            [label]="props.addButton"
            (click)="addRow()"
            [icon]="props.buttonIcon || 'pi pi-plus'"
            [ngClass]="
              'inline-block mt-2 max-md:mb-2 sticky left-0 ' + props.buttonClass
            "
            size="small"
          ></p-button>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRepeatTableTypeComponent
  extends FieldArrayType<FieldArrayTypeConfig<RepeatTableProps>>
  implements OnInit
{
  labels: Map<string, string> = new Map();
  sortMenuModel: MenuItem[] = [];
  hiddenRows: Set<number> = new Set();

  override defaultOptions?:
    | Partial<FieldArrayTypeConfig<RepeatTableProps>>
    | undefined = {
    props: {
      addButton: 'Pridať',
      removeText: '',
      tableClass: 'w-full table-auto',
    },
  };

  fields = () => (this.field.fieldArray as FormlyFieldConfig).fieldGroup;

  removeLabel = (field: FormlyFieldConfig) => {
    if (field.props) {
      field.props.label = undefined;
    }
    return field;
  };

  addRow() {
    const lastItem = this.model?.slice(-1) ?? {};

    if (this.props.addButtonAction) {
      this.props.addButtonAction(this.field, lastItem);
      return;
    }

    const newItem = this.props.newItem?.(lastItem) ?? {};
    this.add(undefined, newItem, { markAsDirty: true });
  }

  insertRowAt(index: number, item?: any) {
    const newItem = item ?? this.props.newItem?.() ?? {};
    this.add(index, newItem, { markAsDirty: true });
  }

  copyRowAt(index: number) {
    if (!this.props.copyItem) {
      return;
    }

    const origin = this.formControl.getRawValue()?.[index] ?? {};
    this.props.copyItem(this.field, {
      origin,
      index,
      add: (...args: Parameters<FieldArrayType['add']>) => {
        this.add(...args);

        const newIndex = args[0] ?? this.formControl.length - 1;
        const newField = this.field.fieldGroup?.[newIndex];

        return newField;
      },
    });
  }

  onRowKeydown(event: KeyboardEvent, index: number) {
    const hasModifierKey = event.ctrlKey || event.metaKey;

    if (this.props.copyItem && hasModifierKey && event.key === 'd') {
      event.preventDefault();
      this.copyRowAt(index);
    } else if (
      hasModifierKey &&
      event.key === 'x' &&
      (window.getSelection()?.toString() ?? '').length === 0 &&
      !this.props.removeHidden &&
      !this.props.removeDisabled
    ) {
      event.preventDefault();
      const removed = this.removeRowAt(index);

      toast({
        severity: 'info',
        summary: 'Riadok bol odstránený.',
        life: 5000,
        actions: [
          {
            label: 'Späť',
            command: (_msg, close) => {
              this.insertRowAt(index, removed);
              close();
            },
          },
        ],
      });
    }
  }

  removeRowAt(index: number) {
    const item = this.formControl.getRawValue()?.[index];

    this.remove(index);

    if (this.props.onDelete) {
      this.props.onDelete(this, index);
    }

    return item;
  }

  getLabel(key: any) {
    return this.labels.get(key);
  }

  ngOnInit(): void {
    this.labels = new Map<string, string>(
      this.fields()
        ?.map((field) => [field?.key, field?.props?.label])
        .filter(
          (entry): entry is [string, string] =>
            typeof entry[0] === 'string' && typeof entry[1] === 'string',
        ),
    );
    this.sortMenuModel = Array.from(this.labels.keys()).map<MenuItem>(
      (key) => ({
        label: this.labels.get(key) ?? key,
        items: [
          {
            label: 'Vzostupne',
            command: () => this.sortTable(key, 'asc'),
          },
          {
            label: 'Zostupne',
            command: () => this.sortTable(key, 'desc'),
          },
        ],
      }),
    );
  }

  getCellClass(field?: FormlyFieldConfig) {
    if (this.props?.cellClass) {
      if (typeof this.props?.cellClass === 'string') {
        return this.props?.cellClass;
      } else if (field) {
        return this.props?.cellClass?.[field.key as string] ?? undefined;
      }
    }
    return undefined;
  }

  getHeaderCellClass(field?: FormlyFieldConfig) {
    if (this.props?.headerCellClass) {
      if (typeof this.props?.headerCellClass === 'string') {
        return this.props?.headerCellClass;
      } else if (field) {
        return this.props?.headerCellClass?.[field.key as string] ?? undefined;
      }
    }
    return undefined;
  }

  applyFilter(value: string | null) {
    this.hiddenRows.clear();
    const term = (value ?? '').toLowerCase().trim();
    if (!term) return;

    const rows = this.formControl.value as any[];
    rows?.forEach((row, index) => {
      const match = Object.values(row ?? {}).some((val) =>
        String(val ?? '')
          .toLowerCase()
          .includes(term),
      );
      if (!match) {
        this.hiddenRows.add(index);
      }
    });
  }

  isRowHidden(index: number): boolean {
    return this.hiddenRows.has(index);
  }

  sortTable(key: string, dir: 'asc' | 'desc' = 'asc') {
    const current = this.formControl.value as any[];

    const sorted = [...current].sort((a, b) => {
      const aValue = (a?.[key] ?? '').toString();
      const bValue = (b?.[key] ?? '').toString();
      const compare = aValue.localeCompare(bValue);

      return compare * (dir === 'asc' ? 1 : -1);
    });

    this.formControl.patchValue(sorted);
  }
}
